<template>
  <div class="text-gray-900 bg-white dark:bg-zinc-950 dark:text-zinc-100">
    <!-- PLANS SECTION -->
    <section class="max-w-screen-xl px-6 py-20 mx-auto">
      <h2
        class="p-2 mb-16 text-5xl font-extrabold text-center text-transparent bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text"
      >
        {{ $t("services.plans.title") }}
      </h2>

      <div
        class="grid grid-cols-1 gap-10 mlg:gap-14"
        :class="{
          'md:grid-cols-2': loadingPlans ? 2 : plans.length % 2 === 0,
          'md:grid-cols-3': loadingPlans ? 3 : plans.length % 3 === 0,
        }"
      >
        <!-- Loading Skeletons -->
        <template v-if="loadingPlans">
          <Card
            v-for="n in 3"
            :key="'skeleton-' + n"
            class="flex flex-col p-8 bg-white shadow-xl dark:bg-zinc-950 rounded-3xl"
          >
            <CardHeader class="space-y-3 text-center">
              <Skeleton class="h-8 w-32 mx-auto rounded" />
              <Skeleton class="h-5 w-48 mx-auto rounded" />
            </CardHeader>
            <CardContent class="flex-1 mt-6 space-y-3">
              <div
                v-for="n in 4"
                :key="'item-' + n"
                class="flex items-center gap-3"
              >
                <Skeleton class="h-5 w-5 rounded shrink-0" />
                <Skeleton class="h-4 w-40 rounded" />
              </div>
            </CardContent>
            <CardFooter
              class="flex flex-col items-center gap-5 pt-6 border-t border-zinc-200 dark:border-zinc-800"
            >
              <Skeleton class="h-4 w-24 rounded" />
              <Skeleton class="h-4 w-32 rounded" />
              <Skeleton class="h-12 w-24 rounded" />
              <Skeleton class="h-10 w-full rounded" />
            </CardFooter>
          </Card>
        </template>

        <!-- Plan Cards -->
        <Card
          v-for="(plan, index) in plans"
          v-else
          :key="index"
          class="flex flex-col p-8 transition-all duration-300 bg-white shadow-xl dark:bg-zinc-950 rounded-3xl hover:-translate-y-2 hover:shadow-2xl"
        >
          <CardHeader class="space-y-2 text-center">
            <h3 class="text-2xl font-bold tracking-tight">
              {{ plan.title }}
            </h3>
            <p class="text-zinc-500 dark:text-zinc-400">
              {{ plan.subtitle }}
            </p>
          </CardHeader>

          <CardContent class="flex-1 mt-6 space-y-4">
            <ul class="space-y-3">
              <li
                v-for="(feature, fIndex) in plan.details"
                :key="fIndex"
                class="flex items-center gap-3 text-gray-700 dark:text-zinc-300"
              >
                <CheckIcon class="w-5 h-5 text-green-500 shrink-0" />
                <span class="leading-snug">{{ feature }}</span>
              </li>
            </ul>
          </CardContent>

          <CardFooter
            class="flex flex-col items-center gap-5 pt-6 border-t border-zinc-200 dark:border-zinc-800"
          >
            <p class="text-zinc-600 dark:text-zinc-400">{{ plan.tip }}</p>
            <small class="italic text-zinc-500">{{ plan.note }}</small>
            <p
              v-if="!loadingPrices"
              class="text-4xl font-extrabold text-gray-900 dark:text-zinc-50"
            >
              {{ plan.price }} <span>€</span>
            </p>
            <Skeleton v-else class="h-[50px] w-[100px] rounded-xl" />

            <Button
              size="lg"
              class="w-full mt-2 font-semibold text-white transition-all duration-200 shadow-lg cursor-pointer bg-gradient-to-r from-blue-500 to-purple-500 hover:from-purple-500 hover:to-pink-500 rounded-xl"
              @click="
                sendEmail({
                  to: emailReceivers,
                  cc: emailCCs,
                  plan: plan,
                })
              "
            >
              {{ $t("services.plans.cta") }}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </section>

    <!-- OLD Other Services Section -->
    <!-- <Section class="max-w-screen-xl px-6 py-16 mx-auto">
      <Accordion type="single" collapsible class="w-full">
        <AccordionItem value="services-other">
          
          <AccordionTrigger
            class="flex items-center justify-center gap-3 text-3xl font-bold text-center text-gray-900 shadow-sm cursor-pointer select-none bg-card dark:text-zinc-100"
          >
            {{ $t("services.other.title") }}
          </AccordionTrigger>

          <AccordionContent>
            <Card class="p-6 -mt-4 space-y-10 shadow-xl rounded-2xl">
              <div
                class="grid w-full grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4"
              >
                <div
                  v-for="(section, index) in sections"
                  :key="index"
                  class="flex flex-col space-y-6"
                >
                  <div
                    class="text-2xl font-bold text-center text-gray-900 break-words sm:text-left dark:text-zinc-100"
                  >
                    {{ $t(`sections.${section.key}.mainTitle`) }}
                    <div
                      class="italic font-bold text-zinc-500 dark:text-zinc-400"
                    >
                      {{ section.type }}
                    </div>
                  </div>

                  <Accordion type="single" collapsible class="w-full">
                    <div
                      v-for="(subject, subjectIndex) in section.subjects"
                      :key="subjectIndex"
                    >
                      <AccordionItem :value="subject.accordionValue">
                        <AccordionTrigger
                          class="text-lg text-gray-900 no-underline uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-700 dark:text-zinc-100"
                        >
                          {{
                            $t(
                              `sections.${section.key}.subjects.${subject.key}.title`
                            )
                          }}
                        </AccordionTrigger>
                        <AccordionContent
                          class="text-base text-gray-700 dark:text-zinc-300"
                        >
                          {{
                            $t(
                              `sections.${section.key}.subjects.${subject.key}.content`
                            )
                          }}
                          <p
                            v-if="getPrice(section.key, subject.key)"
                            class="flex items-baseline gap-1 mt-4 italic text-gray-600 dark:text-zinc-400"
                          >
                            <span
                              v-if="
                                $te(
                                  `sections.${section.key}.subjects.${subject.key}.prePriceMessage`
                                )
                              "
                            >
                              {{
                                $t(
                                  `sections.${section.key}.subjects.${subject.key}.prePriceMessage`
                                )
                              }}
                            </span>
                            <span
                              class="font-semibold text-black dark:text-zinc-100"
                            >
                              {{ getPrice(section.key, subject.key) }}
                            </span>
                            <span
                              v-if="
                                $te(
                                  `sections.${section.key}.subjects.${subject.key}.postPriceMessage`
                                )
                              "
                            >
                              {{
                                $t(
                                  `sections.${section.key}.subjects.${subject.key}.postPriceMessage`
                                )
                              }}
                            </span>
                          </p>
                        </AccordionContent>
                      </AccordionItem>
                    </div>
                  </Accordion>
                </div>
              </div>
            </Card>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Section> -->

    <!-- REQUEST QUOTE BUTTON -->
    <section
      class="flex justify-center md:mt-12 md:border-t md:border-zinc-200 dark:border-zinc-800"
    >
      <button
        @click="openQuoteForm"
        class="md:mt-20 w-2/3 cursor-pointer uppercase relative px-10 py-5 text-2xl font-extrabold text-white rounded-3xl bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 shadow-[0_10px_25px_rgba(0,0,0,0.2)] overflow-hidden transition-all duration-500 transform hover:scale-105 hover:shadow-[0_15px_35px_rgba(0,0,0,0.3)]"
      >
        {{ $t("requestQuote") }}
        <!-- efeito glow animado -->
        <span
          class="absolute inset-0 w-full h-full bg-white rounded-3xl opacity-10 blur-xl animate-pulse"
        ></span>
      </button>
    </section>

    <section class="max-w-screen-xl px-6 py-16 mx-auto space-y-12">
      <h2
        class="text-3xl font-extrabold text-center text-gray-900 dark:text-zinc-100"
      >
        {{ $t("services.other.title") }}
      </h2>

      <!-- Loading Skeletons for Sections -->
      <div v-if="loadingServices" class="space-y-12">
        <div
          v-for="n in 4"
          :key="'skeleton-section-' + n"
          class="flex flex-col p-6 space-y-4 bg-white shadow-lg dark:bg-zinc-950 rounded-2xl"
        >
          <Skeleton class="h-6 w-32 rounded" />
          <div class="flex flex-col gap-4 mt-4 sm:flex-row sm:flex-wrap">
            <div
              v-for="m in 3"
              :key="'skeleton-item-' + m"
              class="flex-1 p-4 rounded-lg"
            >
              <Skeleton class="h-5 w-24 rounded mb-2" />
              <Skeleton class="h-4 w-full rounded mb-2" />
              <Skeleton class="h-4 w-32 rounded" />
            </div>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div
        v-else-if="servicesError"
        class="rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 p-6 text-center"
      >
        <p class="text-red-600 dark:text-red-300 font-semibold">
          {{ servicesError }}
        </p>
      </div>

      <!-- Services Content -->
      <div v-else class="space-y-12">
        <!-- Cada Section -->
        <div
          v-for="(section, index) in sections"
          :key="index"
          class="flex flex-col p-6 space-y-4 bg-white shadow-lg dark:bg-zinc-950 rounded-2xl"
        >
          <!-- Section title -->
          <!-- <div
            class="text-xl font-bold text-center text-gray-900 dark:text-zinc-100 sm:text-left"
          >
            {{ $t(`sections.${section.key}.mainTitle`) }}
          </div>
          <div
            class="italic text-center text-zinc-500 dark:text-zinc-400 sm:text-left"
          >
            {{ section.type }}
          </div> -->

          <!-- Cards container -->
          <div class="flex flex-col gap-4 mt-4 sm:flex-row sm:flex-wrap">
            <div
              v-for="(subject, sIndex) in section.subjects"
              :key="sIndex"
              class="flex-1 p-4 transition-colors duration-200 rounded-lg bg-gray-50 dark:bg-zinc-800 hover:bg-gray-100 dark:hover:bg-zinc-700"
            >
              <div class="font-semibold text-gray-800 dark:text-zinc-100">
                {{
                  $t(`sections.${section.key}.subjects.${subject.key}.title`)
                }}
              </div>
              <p class="mt-1 text-sm text-gray-600 dark:text-zinc-300">
                {{
                  $t(`sections.${section.key}.subjects.${subject.key}.content`)
                }}
              </p>
              <p
                v-if="getPrice(section.key, subject.key)"
                class="flex items-baseline gap-1 mt-4 italic text-gray-600 dark:text-zinc-400"
              >
                <span
                  v-if="
                    $te(
                      `sections.${section.key}.subjects.${subject.key}.prePriceMessage`
                    )
                  "
                >
                  {{
                    $t(
                      `sections.${section.key}.subjects.${subject.key}.prePriceMessage`
                    )
                  }}
                </span>
                <span class="font-semibold text-black dark:text-zinc-100">
                  {{ getPrice(section.key, subject.key) }}
                </span>
                <span
                  v-if="
                    $te(
                      `sections.${section.key}.subjects.${subject.key}.postPriceMessage`
                    )
                  "
                >
                  {{
                    $t(
                      `sections.${section.key}.subjects.${subject.key}.postPriceMessage`
                    )
                  }}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <ContactButtons class="mt-20 mb-24" />
  </div>
</template>

<script setup lang="ts">
import { serviceSections } from "@/configs/services";
import { type SectionKey } from "@/configs/prices";
import { CheckIcon } from "lucide-vue-next";
import { ref, computed, onMounted } from "vue";

const i18n = useI18n();
const emailReceivers = ["studio@highfield.pt", "studiohighfield@gmail.com"];
const emailCCs = ["renato.ornelas@live.com", "renatopxo@gmail.com"];
const formLink = "https://form.jotform.com/253531146542048"; // muda aqui o link futuramente

// ------------------------------
// reactive prices store
const pricesData = ref<Record<string, any> | null>(null);
const loadingPrices = ref(true);
const loadingPlans = ref(true);
const loadingServices = ref(true);
const plansData = ref<Record<string, any> | null>(null);
const servicesData = ref<Record<string, any> | null>(null);
const servicesError = ref<string | null>(null);

async function fetchPrices() {
  if (pricesData.value) return;
  loadingPrices.value = true;

  // Force hide skeleton after 1s
  const timeoutId = setTimeout(() => {
    loadingPrices.value = false;
  }, 1000);

  try {
    const res = await fetch("/api/prices");
    if (!res.ok) throw new Error(`Failed to fetch prices: ${res.statusText}`);
    pricesData.value = await res.json();
  } catch (err) {
    console.error(err);
    pricesData.value = null;
  } finally {
    clearTimeout(timeoutId);
    loadingPrices.value = false;
  }
}

async function fetchPlans() {
  if (plansData.value) return;
  loadingPlans.value = true;

  // Force hide skeleton after 1s
  const timeoutId = setTimeout(() => {
    loadingPlans.value = false;
  }, 1000);

  try {
    const res = await fetch("/api/plans");
    if (!res.ok) throw new Error(`Failed to fetch plans: ${res.statusText}`);
    plansData.value = await res.json();
  } catch (err) {
    console.error(err);
    plansData.value = null;
  } finally {
    clearTimeout(timeoutId);
    loadingPlans.value = false;
  }
}

async function fetchServices() {
  if (servicesData.value) return;
  loadingServices.value = true;
  servicesError.value = null;

  // Force hide skeleton after 1s
  const timeoutId = setTimeout(() => {
    loadingServices.value = false;
  }, 1000);

  try {
    const res = await fetch("/api/services");
    if (!res.ok) {
      throw new Error(`Failed to fetch services: ${res.statusText}`);
    }
    servicesData.value = await res.json();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    servicesError.value = message;
    console.error(err);
    servicesData.value = null;
  } finally {
    clearTimeout(timeoutId);
    loadingServices.value = false;
  }
}

// ------------------------------
// getPrice function
const getPrice = (sectionKey: SectionKey, subjectKey: string) => {
  if (loadingPrices.value || !pricesData.value) return "";

  const price =
    (pricesData.value.sections as any)?.[sectionKey]?.[subjectKey] ??
    (pricesData.value.school.classes as any)?.[subjectKey] ??
    null;

  return price !== null ? price : i18n.t("nullPrice");
};

// ------------------------------
// Sections for display
const sections = computed(() => {
  if (!servicesData.value) return [];

  const map = new Map();

  servicesData.value.forEach((service: any) => {
    if (!map.has(service.section_key)) {
      map.set(service.section_key, {
        key: service.section_key,
        type: i18n.t(service.section_type_key),
        subjects: [],
      });
    }

    map.get(service.section_key).subjects.push({
      key: service.subject_key,
      defaultOpened: service.default_opened,
      price: service.price,
    });
  });

  return Array.from(map.values());
});

const getDefaultOpenedSubject = (subjects: any[]) => {
  const defaultOpened = subjects.find((s) => s.defaultOpened);
  return defaultOpened ? defaultOpened.accordionValue : undefined;
};

// ------------------------------
const plans = computed(() => {
  if (!plansData.value || loadingPrices.value) return [];

  return plansData.value.map((plan: any) => {
    const base = "services.plans.";

    const titleKey = `${base}${plan.title}`;
    const subtitleKey = `${base}${plan.subTitle}`;
    const noteKey = `${base}${plan.note}`;
    const tipKey = `${base}${plan.tip}`;
    const detailsKey = `${base}${plan.details}`;

    return {
      key: plan.key,

      title: i18n.te(titleKey) ? i18n.t(titleKey) : titleKey,
      subtitle: i18n.te(subtitleKey) ? i18n.t(subtitleKey) : subtitleKey,
      note: i18n.te(noteKey) ? i18n.t(noteKey) : noteKey,
      tip: i18n.te(tipKey) ? i18n.t(tipKey) : tipKey,

      price: plan.price,

      details: i18n.te(detailsKey)
        ? (i18n.t(detailsKey) as string).split(".").map((d) => d.trim())
        : [],
    };
  });
});

// ------------------------------
// Email helper
interface EmailOptions {
  to: string[];
  cc?: string[];
  plan: any;
}

const sendEmail = ({ to, cc = [], plan }: EmailOptions) => {
  const planTitle = plan.title;
  const planPrice = plan.price;
  const planDetails = plan.details.length ? plan.details.join("\n- ") : "N/A";

  const subject = i18n.t("emailTemplates.subject", { plan: planTitle });
  const body = i18n.t("emailTemplates.body", {
    plan: planTitle,
    price: planPrice,
    details: `- ${planDetails}`,
  });

  let mailto = `mailto:${to.join(",")}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;

  if (cc.length) mailto += `&cc=${cc.join(",")}`;

  window.location.href = mailto;
};

async function initData() {
  await fetchPrices();
  await fetchPlans();
  await fetchServices();
}

//function to open quote form link (form)
const openQuoteForm = () => {
  window.open(formLink, "_blank");
};

// ------------------------------
// Fetch prices on mount
onMounted(initData);

// ------------------------------
// SEO meta
useSeoMeta({
  title: i18n.t("menuKeys.services"),
  description:
    "Highfield Music Studio provides a wide range of services to help you bring your ideas to life.",
  keywords:
    "studio, music, instruments, recording, audio equipment, midi, dj gear, microphones, studio equipment",
  ogTitle: "Services | Highfield Music Studio",
  ogDescription:
    "Highfield Music Studio provides a wide range of services to help you bring your ideas to life.",
  ogImage: "/images/logos/studio/1.jpg",
  twitterCard: "summary_large_image",
  twitterTitle: "Services | Highfield Music Studio",
  twitterDescription:
    "Highfield Music Studio provides a wide range of services to help you bring your ideas to life.",
  twitterImage: "/images/media/studio/1.jpg",
});
</script>
